import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, normalizeStyle as _normalizeStyle, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, createVNode as _createVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "g-gantt-rows-container" }

import {
  computed,
  provide,
  ref,
  toRefs,
  useSlots,
  type ComputedRef,
  type Ref,
  type ToRefs
} from "vue"

import GGanttGrid from "./GGanttGrid.vue"
import GGanttLabelColumn from "./GGanttLabelColumn.vue"
import GGanttTimeaxis from "./GGanttTimeaxis.vue"
import GGanttBarTooltip from "./GGanttBarTooltip.vue"
import GGanttCurrentTime from "./GGanttCurrentTime.vue"

import type { GanttBarObject } from "../types"
import type { ColorSchemeKey } from "../color-schemes"

import { useElementSize } from "@vueuse/core"
import { DEFAULT_DATE_FORMAT } from "../composables/useDayjsHelper"
import { colorSchemes, type ColorScheme } from "../color-schemes"
import {
  CHART_ROWS_KEY,
  CONFIG_KEY,
  EMIT_BAR_EVENT_KEY,
  type ChartRow
} from "../provider/symbols"

export interface GGanttChartProps {
  chartStart: string | Date
  chartEnd: string | Date
  precision?: "hour" | "day" | "date" | "week" | "month"
  barStart: string
  barEnd: string
  currentTime?: boolean
  currentTimeLabel?: string
  dateFormat?: string | false
  width?: string
  hideTimeaxis?: boolean
  colorScheme?: ColorSchemeKey | ColorScheme
  grid?: boolean
  pushOnOverlap?: boolean
  noOverlap?: boolean
  rowHeight?: number
  highlightedUnits?: number[]
  font?: string
  labelColumnTitle?: string
  labelColumnWidth?: string
}

export type GGanttChartConfig = ToRefs<Required<GGanttChartProps>> & {
  colors: ComputedRef<ColorScheme>
  chartSize: {
    width: Ref<number>
    height: Ref<number>
  }
}

// eslint-disable-next-line no-undef

export default /*#__PURE__*/_defineComponent({
  __name: 'GGanttChart',
  props: {
    chartStart: {},
    chartEnd: {},
    precision: { default: "day" },
    barStart: {},
    barEnd: {},
    currentTime: { type: Boolean },
    currentTimeLabel: { default: "" },
    dateFormat: { type: [String, Boolean], default: DEFAULT_DATE_FORMAT },
    width: { default: "100%" },
    hideTimeaxis: { type: Boolean, default: false },
    colorScheme: { default: "roterhahn" },
    grid: { type: Boolean, default: false },
    pushOnOverlap: { type: Boolean, default: false },
    noOverlap: { type: Boolean, default: false },
    rowHeight: { default: 40 },
    highlightedUnits: { default: () => [] },
    font: { default: "inherit" },
    labelColumnTitle: { default: "" },
    labelColumnWidth: { default: "150px" }
  },
  emits: ["click-bar", "mousedown-bar", "mouseup-bar", "dblclick-bar", "mouseenter-bar", "mouseleave-bar", "dragstart-bar", "drag-bar", "dragend-bar", "contextmenu-bar"],
  setup(__props: any, { emit: __emit }) {

const props = __props

// eslint-disable-next-line no-undef
const emit = __emit

const { width, font, colorScheme } = toRefs(props)

const slots = useSlots()
const colors = computed(() =>
  typeof colorScheme.value !== "string"
    ? colorScheme.value
    : colorSchemes[colorScheme.value as ColorSchemeKey] || colorSchemes.default
)
const getChartRows = () => {
  const defaultSlot = slots.default?.()
  const allBars: ChartRow[] = []

  if (!defaultSlot) {
    return allBars
  }
  defaultSlot.forEach((child) => {
    if (child.props?.bars) {
      const { label, bars } = child.props
      allBars.push({ label, bars })
      // if using v-for to generate rows, rows will be children of a single "fragment" v-node:
    } else if (Array.isArray(child.children)) {
      child.children.forEach((grandchild) => {
        const granchildNode = grandchild as {
          props?: ChartRow
        }
        if (granchildNode?.props?.bars) {
          const { label, bars } = granchildNode.props
          allBars.push({ label, bars })
        }
      })
    }
  })
  return allBars
}

const showTooltip = ref(false)
const isDragging = ref(false)
const tooltipBar = ref<GanttBarObject | undefined>(undefined)
let tooltipTimeoutId: ReturnType<typeof setTimeout>
const initTooltip = (bar: GanttBarObject) => {
  if (tooltipTimeoutId) {
    clearTimeout(tooltipTimeoutId)
  }
  tooltipTimeoutId = setTimeout(() => {
    showTooltip.value = true
  }, 800)
  tooltipBar.value = bar
}

const clearTooltip = () => {
  clearTimeout(tooltipTimeoutId)
  showTooltip.value = false
}

const emitBarEvent = (
  e: MouseEvent,
  bar: GanttBarObject,
  datetime?: string | Date,
  movedBars?: Map<GanttBarObject, { oldStart: string; oldEnd: string }>
) => {
  switch (e.type) {
    case "click":
      emit("click-bar", { bar, e, datetime })
      break
    case "mousedown":
      emit("mousedown-bar", { bar, e, datetime })
      break
    case "mouseup":
      emit("mouseup-bar", { bar, e, datetime })
      break
    case "dblclick":
      emit("dblclick-bar", { bar, e, datetime })
      break
    case "mouseenter":
      initTooltip(bar)
      emit("mouseenter-bar", { bar, e })
      break
    case "mouseleave":
      clearTooltip()
      emit("mouseleave-bar", { bar, e })
      break
    case "dragstart":
      isDragging.value = true
      emit("dragstart-bar", { bar, e })
      break
    case "drag":
      emit("drag-bar", { bar, e })
      break
    case "dragend":
      isDragging.value = false
      emit("dragend-bar", { bar, e, movedBars })
      break
    case "contextmenu":
      emit("contextmenu-bar", { bar, e, datetime })
      break
  }
}

const ganttChart = ref<HTMLElement | null>(null)
const chartSize = useElementSize(ganttChart)

provide(CHART_ROWS_KEY, getChartRows)
provide(CONFIG_KEY, {
  ...toRefs(props),
  colors,
  chartSize
})
provide(EMIT_BAR_EVENT_KEY, emitBarEvent)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", {
      class: _normalizeClass([{ 'labels-in-column': !!_ctx.labelColumnTitle }])
    }, [
      (_ctx.labelColumnTitle)
        ? (_openBlock(), _createBlock(GGanttLabelColumn, {
            key: 0,
            style: _normalizeStyle({
          width: _ctx.labelColumnWidth
        })
          }, {
            "label-column-title": _withCtx(() => [
              _renderSlot(_ctx.$slots, "label-column-title")
            ]),
            "label-column-row": _withCtx(({ label }) => [
              _renderSlot(_ctx.$slots, "label-column-row", { label: label })
            ]),
            _: 3
          }, 8, ["style"]))
        : _createCommentVNode("", true),
      _createElementVNode("div", {
        ref_key: "ganttChart",
        ref: ganttChart,
        class: _normalizeClass(['g-gantt-chart', { 'with-column': _ctx.labelColumnTitle }]),
        style: _normalizeStyle({ width: _unref(width), background: colors.value.background, fontFamily: _unref(font) })
      }, [
        (!_ctx.hideTimeaxis)
          ? (_openBlock(), _createBlock(GGanttTimeaxis, { key: 0 }, {
              "upper-timeunit": _withCtx(({ label, value, date }) => [
                _renderSlot(_ctx.$slots, "upper-timeunit", {
                  label: label,
                  value: value,
                  date: date
                })
              ]),
              timeunit: _withCtx(({ label, value, date }) => [
                _renderSlot(_ctx.$slots, "timeunit", {
                  label: label,
                  value: value,
                  date: date
                })
              ]),
              _: 3
            }))
          : _createCommentVNode("", true),
        (_ctx.grid)
          ? (_openBlock(), _createBlock(GGanttGrid, {
              key: 1,
              "highlighted-units": _ctx.highlightedUnits
            }, null, 8, ["highlighted-units"]))
          : _createCommentVNode("", true),
        (_ctx.currentTime)
          ? (_openBlock(), _createBlock(GGanttCurrentTime, { key: 2 }, {
              "current-time-label": _withCtx(() => [
                _renderSlot(_ctx.$slots, "current-time-label")
              ]),
              _: 3
            }))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_1, [
          _renderSlot(_ctx.$slots, "default")
        ])
      ], 6)
    ], 2),
    _createVNode(GGanttBarTooltip, {
      "model-value": showTooltip.value || isDragging.value,
      bar: tooltipBar.value
    }, {
      default: _withCtx(() => [
        _renderSlot(_ctx.$slots, "bar-tooltip", { bar: tooltipBar.value })
      ]),
      _: 3
    }, 8, ["model-value", "bar"])
  ]))
}
}

})